import {
  createContext,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

/**
 * Each accent preset defines a hue (in oklch colour space) that drives all
 * `--primary-*` CSS custom properties via the `--accent-hue` variable.
 */
export interface AccentPreset {
  /** Human-readable label for the UI. */
  label: string;
  /** Hue value on the oklch colour wheel (0-360). */
  hue: number;
}

export const ACCENT_PRESETS = {
  cobalt: { label: "Cobalt", hue: 262.881 },
  indigo: { label: "Indigo", hue: 280 },
  purple: { label: "Purple", hue: 305 },
  rose: { label: "Rose", hue: 355 },
  red: { label: "Red", hue: 25 },
  orange: { label: "Orange", hue: 60 },
  green: { label: "Green", hue: 150 },
  teal: { label: "Teal", hue: 185 },
} as const satisfies Record<string, AccentPreset>;

export type AccentPresetKey = keyof typeof ACCENT_PRESETS;

export const DEFAULT_ACCENT: AccentPresetKey = "cobalt";

const ACCENT_STORAGE_KEY = "cheatsheetz-accent";

export interface AccentColorContextValue {
  /** Currently selected accent preset key. */
  accent: AccentPresetKey;
  /** Update the accent colour. Persisted to localStorage. */
  setAccent: (key: AccentPresetKey) => void;
}

function getStoredAccent(): AccentPresetKey {
  try {
    const stored = localStorage.getItem(ACCENT_STORAGE_KEY);
    if (stored && stored in ACCENT_PRESETS) return stored as AccentPresetKey;
  } catch {
    // localStorage may not be available in tests / SSR
  }
  return DEFAULT_ACCENT;
}

function applyAccentHue(hue: number) {
  document.documentElement.style.setProperty("--accent-hue", String(hue));
}

export const AccentColorContext = createContext<AccentColorContextValue | null>(null);

export function AccentColorProvider({ children }: { children: ReactNode }) {
  const [accent, setAccentState] = useState<AccentPresetKey>(getStoredAccent);

  useEffect(() => {
    applyAccentHue(ACCENT_PRESETS[accent].hue);
  }, [accent]);

  const setAccent = useCallback((key: AccentPresetKey) => {
    setAccentState(key);
    try {
      localStorage.setItem(ACCENT_STORAGE_KEY, key);
    } catch {
      // localStorage may not be available in tests / SSR
    }
  }, []);

  const value = useMemo(() => ({ accent, setAccent }), [accent, setAccent]);

  return (
    <AccentColorContext.Provider value={value}>{children}</AccentColorContext.Provider>
  );
}
